import React from 'react';
import './TableSkeleton.scss';

/**
 * Modern TableSkeleton Component
 * A contemporary loading skeleton with shimmer effects for table rows during pagination.
 *
 * @param {number} rows - Number of skeleton rows to display
 * @param {number} columns - Number of columns in the table
 * @param {boolean} tbodyOnly - If true, renders only tbody (for use within existing table)
 * @param {boolean} infiniteScroll - If true, renders skeleton for infinite scroll loading
 * @returns {JSX.Element} Returns the modern table skeleton component
 */
const TableSkeleton = ({ rows, columns = 7, tbodyOnly = false, infiniteScroll = false }) => {
  // Use standard row height for calculations
  const rowHeight = 51; // 50px + 1px border

  // Calculate rows based on the requested number of rows
  const calculatedRows = rows;
  
  // Generate different skeleton content types for each column
  const getSkeletonContent = (colIndex, rowIndex) => {
    const contentTypes = [
      'text',      // Business Name - longer text
      'text',      // Email - medium text
      'text',      // Sales Rep/Account Ex - medium text
      'text',      // GW ID - short text
      'date',      // Date Submitted - short text
      'date',      // Date Approved - short text
      'date',      // Updated Date - short text
      'badge'      // Status - pill shape
    ];
    
    const contentType = contentTypes[colIndex] || 'text';
    const isEvenRow = rowIndex % 2 === 0;
    
    return {
      type: contentType,
      width: getContentWidth(contentType, isEvenRow),
      height: getContentHeight(contentType)
    };
  };

  const getContentWidth = (type, isEvenRow) => {
    const variations = {
      text: isEvenRow ? ['60%', '80%', '70%'] : ['50%', '90%', '65%'],
      date: isEvenRow ? ['40%', '50%'] : ['35%', '45%'],
      badge: isEvenRow ? ['30%', '40%'] : ['25%', '35%']
    };
    
    const options = variations[type] || variations.text;
    return options[Math.floor(Math.random() * options.length)];
  };

  const getContentHeight = (type) => {
    return type === 'badge' ? '20px' : '12px';
  };

  // Render skeleton rows for tbody with modern animations
  const skeletonRows = Array.from({ length: calculatedRows }, (_, rowIndex) => (
    <tr key={`skeleton-row-${rowIndex}`} className="skeleton-row modern-skeleton-row">
      {Array.from({ length: columns }, (_, colIndex) => {
        const content = getSkeletonContent(colIndex, rowIndex);
        return (
          <td key={`skeleton-cell-${rowIndex}-${colIndex}`} className="skeleton-cell-wrapper">
            <div className="skeleton-cell">
              <div 
                className={`skeleton-content skeleton-${content.type}`}
                style={{
                  width: content.width,
                  height: content.height,
                  animationDelay: `${(rowIndex * 0.1) + (colIndex * 0.05)}s`
                }}
              ></div>
            </div>
          </td>
        );
      })}
    </tr>
  ));

  // If infiniteScroll is true, render skeleton for infinite scroll
  if (infiniteScroll) {
    return (
      <tbody className="infinite-scroll-skeleton-tbody">
        {skeletonRows}
      </tbody>
    );
  }

  // If tbodyOnly is true, render just the tbody
  if (tbodyOnly) {
    return (
      <tbody>
        {skeletonRows}
      </tbody>
    );
  }
  
  // Full table skeleton (original behavior)
  return (
    <div className="table-wrapper">
      <div className="table-responsive custom-table position-relative">
        <div className="modern-table-skeleton">
          <table className="table table-sticky-header">
            <thead className="sticky-header">
              <tr>
                {Array.from({ length: columns }, (_, index) => (
                  <th key={`skeleton-header-${index}`} scope="col">
                    <div className="skeleton-header"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {skeletonRows}
            </tbody>
          </table>
          
          {/* Shimmer overlay effect */}
          <div className="skeleton-shimmer"></div>
        </div>
      </div>
    </div>
  );
};

export default TableSkeleton; 